import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Calendar, Target, Zap, Clock, BarChart3, TrendingUp, TrendingDown, Minus, AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, RadialBarChart, RadialBar } from "recharts";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUADRANT_LABELS = {
  "hi-impact-lo-effort": "HI / Lo Eff",
  "hi-impact-med-effort": "HI / Med",
  "hi-impact-hi-effort": "HI / Hi Eff",
  "med-impact-lo-effort": "Med / Lo Eff",
  "med-impact-med-effort": "Med / Med",
  "med-impact-hi-effort": "Med / Hi Eff",
  "lo-impact-lo-effort": "Lo / Lo Eff",
  "lo-impact-med-effort": "Lo / Med",
  "lo-impact-hi-effort": "Lo / Hi Eff",
  "inbox": "Inbox",
};

const CATEGORY_COLORS = {
  daily: "#A8C97A",
  weekly: "#C9A84C",
  monthly: "#A8B5B2",
};

const QUADRANT_COLORS = [
  "#A8C97A", "#7A9E7E", "#C9A84C", "#D4B483",
  "#A8B5B2", "#C97D8A", "#4A6741", "#3D5C4A", "#B5697A", "#546E5A",
];

function TrendArrow({ current, previous }) {
  if (previous === 0 && current === 0) return <Minus size={13} className="trend-neutral" />;
  if (previous === 0) return <span className="trend-up"><TrendingUp size={13} /> New</span>;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct > 0) return <span className="trend-up"><TrendingUp size={13} /> +{pct}%</span>;
  if (pct < 0) return <span className="trend-down"><TrendingDown size={13} /> {pct}%</span>;
  return <Minus size={13} className="trend-neutral" />;
}

function formatHours(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d ${Math.round(h % 24)}h`;
}

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  return date.toISOString().split("T")[0];
}

function getSunday(mondayStr) {
  const d = new Date(mondayStr);
  d.setDate(d.getDate() + 6);
  return d.toISOString().split("T")[0];
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="report-chart-tooltip">
      <div className="report-chart-tooltip-label">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey || p.name} className="report-chart-tooltip-value" style={{ color: p.fill || p.color }}>
          {p.value} {p.name || "tasks"}
        </div>
      ))}
    </div>
  );
};

const CHART_BODY_HEIGHT_SM = { height: 200 };
const CHART_BODY_HEIGHT_LG = { height: 220 };
const BAR_CHART_MARGIN = { top: 4, right: 4, bottom: 4, left: -20 };
const X_TICK_STYLE = { fill: "#A8B5B2", fontSize: 10, fontFamily: "Azeret Mono" };
const Y_TICK_STYLE = { fill: "#A8B5B2", fontSize: 10 };
const CURSOR_STYLE = { fill: "rgba(122,158,126,0.06)" };
const RADIAL_BG = { fill: "rgba(122,158,126,0.1)" };

export default function ReportsPage({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => getMonday(new Date()));
  const [endDate, setEndDate] = useState(() => getSunday(getMonday(new Date())));

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/summary`, {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(res.data);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleWeekNav = (direction) => {
    const d = new Date(startDate);
    d.setDate(d.getDate() + (direction * 7));
    const newMonday = d.toISOString().split("T")[0];
    setStartDate(newMonday);
    setEndDate(getSunday(newMonday));
  };

  const handleThisWeek = () => {
    const m = getMonday(new Date());
    setStartDate(m);
    setEndDate(getSunday(m));
  };

  // Chart data
  const quadrantData = data ? Object.entries(data.by_quadrant).map(([key, val]) => ({
    name: QUADRANT_LABELS[key] || key,
    value: val,
    fullName: key,
  })).sort((a, b) => b.value - a.value) : [];

  const categoryData = data ? Object.entries(data.by_category).map(([key, val]) => ({
    name: key,
    value: val,
    fill: CATEGORY_COLORS[key] || "#A8B5B2",
  })) : [];

  const completionPct = data ? Math.round(data.completion_rate * 100) : 0;
  const radialData = [{ name: "rate", value: completionPct, fill: completionPct >= 70 ? "#A8C97A" : completionPct >= 40 ? "#C9A84C" : "#C97D8A" }];

  return (
    <div className="reports-page" data-testid="reports-page">
      <Helmet><title>Reports - Prism Planner</title></Helmet>

      <div className="reports-page-header">
        <button className="reports-back-btn" onClick={onBack} data-testid="reports-back-btn">
          <ArrowLeft size={16} /> Back to Board
        </button>
        <h1 className="reports-page-title">Productivity Report</h1>
      </div>

      {/* Date controls */}
      <div className="reports-date-bar">
        <button className="reports-nav-btn" onClick={() => handleWeekNav(-1)} data-testid="report-prev-week">&larr; Prev</button>
        <div className="reports-date-range">
          <Calendar size={14} />
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="reports-date-input" data-testid="report-start-date" />
          <span>to</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="reports-date-input" data-testid="report-end-date" />
        </div>
        <button className="reports-nav-btn" onClick={handleThisWeek} data-testid="report-this-week">This Week</button>
        <button className="reports-nav-btn" onClick={() => handleWeekNav(1)} data-testid="report-next-week">Next &rarr;</button>
      </div>

      {loading ? (
        <div className="reports-loading">Loading report...</div>
      ) : !data ? (
        <div className="reports-empty">
          <AlertTriangle size={24} />
          <span>No report data available</span>
        </div>
      ) : (
        <>
          {/* Stat cards row */}
          <div className="reports-stat-row">
            <div className="reports-stat-card" data-testid="full-stat-completed">
              <div className="reports-stat-card-icon completed"><Target size={18} /></div>
              <div className="reports-stat-card-body">
                <div className="reports-stat-card-value">{data.tasks_completed}</div>
                <div className="reports-stat-card-label">Tasks Completed</div>
                <div className="reports-stat-card-trend">
                  <TrendArrow current={data.tasks_completed} previous={data.prev_tasks_completed} />
                  <span>vs {data.prev_tasks_completed} prev period</span>
                </div>
              </div>
            </div>

            <div className="reports-stat-card" data-testid="full-stat-created">
              <div className="reports-stat-card-icon created"><Zap size={18} /></div>
              <div className="reports-stat-card-body">
                <div className="reports-stat-card-value">{data.tasks_created}</div>
                <div className="reports-stat-card-label">Tasks Created</div>
                <div className="reports-stat-card-trend">
                  <TrendArrow current={data.tasks_created} previous={data.prev_tasks_created} />
                  <span>vs {data.prev_tasks_created} prev period</span>
                </div>
              </div>
            </div>

            <div className="reports-stat-card" data-testid="full-stat-avg-time">
              <div className="reports-stat-card-icon time"><Clock size={18} /></div>
              <div className="reports-stat-card-body">
                <div className="reports-stat-card-value">{formatHours(data.avg_completion_hours)}</div>
                <div className="reports-stat-card-label">Avg. Completion Time</div>
              </div>
            </div>

            <div className="reports-stat-card" data-testid="full-stat-urgent">
              <div className="reports-stat-card-icon urgent"><AlertTriangle size={18} /></div>
              <div className="reports-stat-card-body">
                <div className="reports-stat-card-value">{data.urgent_stats.completed}/{data.urgent_stats.created}</div>
                <div className="reports-stat-card-label">Urgent Done/Total</div>
              </div>
            </div>
          </div>

          {/* Charts row */}
          <div className="reports-charts-row">
            {/* Completion Rate Ring */}
            <div className="reports-chart-card" data-testid="chart-completion-rate">
              <div className="reports-chart-title">Completion Rate</div>
              <div className="reports-chart-body" style={CHART_BODY_HEIGHT_SM}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" startAngle={90} endAngle={-270} data={radialData}>
                    <RadialBar dataKey="value" cornerRadius={8} background={RADIAL_BG} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="reports-ring-center">
                  <span className="reports-ring-value">{completionPct}%</span>
                  <span className="reports-ring-label">done</span>
                </div>
              </div>
              <div className="reports-chart-footer">{data.tasks_completed} of {data.tasks_created} tasks</div>
            </div>

            {/* Category Pie Chart */}
            <div className="reports-chart-card" data-testid="chart-category">
              <div className="reports-chart-title">By Category</div>
              <div className="reports-chart-body" style={CHART_BODY_HEIGHT_SM}>
                {categoryData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={categoryData} dataKey="value" cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={3} stroke="none">
                        {categoryData.map((entry) => (
                          <Cell key={entry.name} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="reports-chart-empty">No data</div>
                )}
              </div>
              <div className="reports-chart-legend">
                {categoryData.map((c) => (
                  <div key={c.name} className="reports-legend-item">
                    <span className="reports-legend-dot" style={{ background: c.fill }} />
                    <span>{c.name}: {c.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quadrant Bar Chart */}
            <div className="reports-chart-card reports-chart-wide" data-testid="chart-quadrant">
              <div className="reports-chart-title">By Priority Quadrant</div>
              <div className="reports-chart-body" style={CHART_BODY_HEIGHT_LG}>
                {quadrantData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={quadrantData} margin={BAR_CHART_MARGIN}>
                      <XAxis dataKey="name" tick={X_TICK_STYLE} axisLine={false} tickLine={false} />
                      <YAxis tick={Y_TICK_STYLE} axisLine={false} tickLine={false} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} cursor={CURSOR_STYLE} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={36}>
                        {quadrantData.map((entry) => (
                          <Cell key={entry.name} fill={QUADRANT_COLORS[quadrantData.indexOf(entry) % QUADRANT_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="reports-chart-empty">No completed tasks in matrix</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
